import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { getReminderPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { useReminderHook } from "@/hooks/use-reminder.hook.ts";
import { SurveyEditSidebar } from "@/pages/talent/surveys/edit/components/SurveyEditSidebar.tsx";
import {
  SurveyEditPageProps,
  useSurveyEditPage,
} from "@/pages/talent/surveys/edit/pages/survey-edit-pages.tsx";
import { SurveyEditTitleSlot } from "@/pages/talent/surveys/edit/pages/SurveyEditTitleSlot.tsx";
import { SurveyEditPageStep } from "@/pages/talent/surveys/edit/survey-edit.ts";
import { useSurveyUpdateReminders } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettingsPayload } from "@/types/reminder.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  FontAwesomeIcon,
  FullScreenPage,
  LinkTo,
  ListItem,
  Radio,
  Select,
  Stack,
  Switch,
  Tag,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const defaultReminderChannels = [
  ApiNotificationChannelType.in_app,
  ApiNotificationChannelType.email,
  ApiNotificationChannelType.slack,
];

function ensureInAppChannel(channels: ApiNotificationChannelType[]) {
  return Array.from(new Set([ApiNotificationChannelType.in_app, ...channels]));
}

export function SurveyEditNotificationsPage({ survey }: SurveyEditPageProps) {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = useSurveyEditPage();
  const hasSurveyEndDate = !!survey.date_ends_at && new Date(survey.date_ends_at) > new Date();
  const {
    getReminderModeTitle,
    getReminderModeDescription,
    getReminderSettingsDescription,
    getReminderPeriodTitle,
    getReminderEveryTitle,
    calculateReminderCount,
    getReminderModeTagVariant,
    getNotificationChannelTitle,
  } = useReminderHook();

  const updateReminders = useSurveyUpdateReminders();
  const reminderPlaceholders = getReminderPlaceholderOptions();
  const [customizeMessage, setCustomizeMessage] = useState(
    !!survey.reminder_settings?.custom_subject || !!survey.reminder_settings?.custom_body
  );

  const schema = yup.object({
    reminders_enabled: yup.boolean(),
    channels: yup
      .array()
      .of(
        yup
          .mixed<ApiNotificationChannelType>()
          .oneOf(Object.values(ApiNotificationChannelType))
          .required()
      )
      .default(defaultReminderChannels),
    mode: yup
      .mixed<ReminderMode>()
      .oneOf(Object.values(ReminderMode))
      .required()
      .default(ReminderMode.medium),
    custom_subject: yup.string().nullable().default(null),
    custom_body: yup.string().nullable().default(null),
    custom_frequency: yup.number().nullable().default(null),
    custom_period: yup
      .mixed<ReminderPeriod>()
      .oneOf(Object.values(ReminderPeriod))
      .nullable()
      .default(null),
  });

  const { control, handleSubmit, setValue } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      reminders_enabled:
        !!survey.reminder_settings?.mode || survey.reminder_settings?.channels.length === 0,
      channels: ensureInAppChannel(survey.reminder_settings?.channels ?? defaultReminderChannels),
      mode: hasSurveyEndDate
        ? (survey.reminder_settings?.mode ?? ReminderMode.medium)
        : ReminderMode.custom,
      custom_subject: survey.reminder_settings?.custom_subject ?? null,
      custom_body: survey.reminder_settings?.custom_body ?? null,
      custom_frequency: survey.reminder_settings?.custom_frequency ?? (hasSurveyEndDate ? null : 1),
      custom_period:
        survey.reminder_settings?.custom_period ?? (hasSurveyEndDate ? null : ReminderPeriod.day),
    },
  });

  const remindersEnabled = useWatch({ control, name: "reminders_enabled" });
  const channels = useWatch({ control, name: "channels" });
  const mode = useWatch({ control, name: "mode" });
  const customFrequency = useWatch({ control, name: "custom_frequency" });
  const customPeriod = useWatch({ control, name: "custom_period" });

  const daysUntilDeadline = useMemo(() => {
    if (!survey.date_ends_at) return 0;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const due = new Date(survey.date_ends_at);
    due.setHours(0, 0, 0, 0);

    return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }, [survey.date_ends_at]);

  const reminderCount = useMemo(() => {
    if (!mode) return 0;
    return calculateReminderCount(daysUntilDeadline, mode);
  }, [mode, daysUntilDeadline]);

  function ensureCustomReminderSettings() {
    setValue("mode", ReminderMode.custom);
    if (!customFrequency) {
      setValue("custom_frequency", 1);
    }
    if (!customPeriod) {
      setValue("custom_period", ReminderPeriod.day);
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    const payload: ReminderSettingsPayload = data.reminders_enabled
      ? {
          channels: ensureInAppChannel(data.channels),
          custom_subject: customizeMessage && data.custom_subject ? data.custom_subject : null,
          custom_body: customizeMessage && data.custom_body ? data.custom_body : null,
          mode: data.mode,
          ...(data.mode === ReminderMode.custom
            ? {
                custom_frequency: data.custom_frequency,
                custom_period: data.custom_period,
              }
            : {}),
        }
      : {
          channels: [ApiNotificationChannelType.in_app],
          custom_subject: null,
          custom_body: null,
          mode: null,
        };

    try {
      await updateReminders.mutateAsync({
        id: survey.id,
        payload,
      });
      analyticsService.trackEvent(analyticEvents.surveys.edited, {
        [analyticProperties.id]: survey.id,
        [analyticProperties.tab]: SurveyEditPageStep.notifications,
      });
      navigateToStep(survey.id, SurveyEditPageStep.final);
    } catch {}
  });

  const toggleChannel = (
    channel: ApiNotificationChannelType,
    current: ApiNotificationChannelType[],
    onChange: (value: ApiNotificationChannelType[]) => void
  ) => {
    if (channel === ApiNotificationChannelType.in_app) return;
    onChange(
      current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]
    );
  };

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(survey.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(survey.id, SurveyEditPageStep.launch);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit} isLoading={updateReminders.isPending}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={<SurveyEditTitleSlot step={SurveyEditPageStep.notifications} survey={survey} />}
      isCenterVertical={false}
      sidebarSlot={<SurveyEditSidebar step={SurveyEditPageStep.notifications} survey={survey} />}
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"} gap={"xl"}>
          <Title
            header={t("core:notifications_and_reminders")}
            caption={t("core:notifications_and_reminders_description")}
            size={"lg"}
          />
          <Controller
            control={control}
            name="reminders_enabled"
            render={({ field: { value, onChange } }) => (
              <Switch
                title={t("core:turn_on_reminders")}
                checked={value}
                onChange={(checked) => {
                  onChange(checked);
                  if (checked && survey.date_ends_at) {
                    setValue("mode", ReminderMode.medium);
                  }
                  if (checked && !survey.date_ends_at) {
                    ensureCustomReminderSettings();
                  }
                }}
              />
            )}
          />
        </Stack>
        {remindersEnabled && (
          <>
            <Stack className={"card-panel"} gap={"xl"}>
              <Title
                header={t("core:channels")}
                caption={t("core:reminder_channels_description")}
              />

              <Controller
                control={control}
                name="channels"
                render={({ field: { value, onChange } }) => (
                  <Stack gap={"sm"}>
                    <Checkbox
                      title={t("core:in_app")}
                      variant={"card"}
                      checked={value.includes(ApiNotificationChannelType.in_app)}
                      disabled
                    />
                    <Checkbox
                      title={getNotificationChannelTitle(ApiNotificationChannelType.email)}
                      variant={"card"}
                      checked={value.includes(ApiNotificationChannelType.email)}
                      onChange={() =>
                        toggleChannel(ApiNotificationChannelType.email, value, onChange)
                      }
                    />
                    <Checkbox
                      title={getNotificationChannelTitle(ApiNotificationChannelType.slack)}
                      variant={"card"}
                      checked={value.includes(ApiNotificationChannelType.slack)}
                      onChange={() =>
                        toggleChannel(ApiNotificationChannelType.slack, value, onChange)
                      }
                    />
                    <Checkbox
                      title={getNotificationChannelTitle(ApiNotificationChannelType.messenger)}
                      variant={"card"}
                      checked={value.includes(ApiNotificationChannelType.messenger)}
                      onChange={() =>
                        toggleChannel(ApiNotificationChannelType.messenger, value, onChange)
                      }
                    />
                  </Stack>
                )}
              />
            </Stack>
            <Stack className={"card-panel"} gap={"xl"}>
              <Title header={t("core:choose_reminder_frequency")} />

              {!hasSurveyEndDate && (
                <Stack
                  direction={"horizontal"}
                  className={"bg-dark/5 border-dark/5 border rounded-xs p-6"}
                >
                  <ListItem
                    emoji=":information_source:"
                    behavior={"card"}
                    title={t("core:reminder_requires", {
                      needed: survey.date_ends_at
                        ? t("core:reminder_requires_future_date")
                        : t("core:reminder_requires_period"),
                    })}
                    captionSlot={
                      <Stack gap={"sm"}>
                        <Text>
                          {survey.date_ends_at
                            ? t("core:reminder_requires_future_date_description")
                            : t("core:reminder_requires_period_description")}
                        </Text>
                        <LinkTo to={`/surveys/${survey.id}/setup/launch`}>
                          {t("common:set_timeframe")}
                        </LinkTo>
                      </Stack>
                    }
                  />
                </Stack>
              )}

              <Controller
                control={control}
                name="mode"
                render={({ field: { value, onChange } }) => (
                  <Stack gap="sm">
                    {[
                      ReminderMode.light,
                      ReminderMode.medium,
                      ReminderMode.hard,
                      ReminderMode.custom,
                    ].map((item) => (
                      <Radio
                        disabled={!hasSurveyEndDate && item !== ReminderMode.custom}
                        key={`radio-${item}`}
                        value={item}
                        checked={value === item}
                        onChange={() => {
                          if (item === ReminderMode.custom) {
                            ensureCustomReminderSettings();
                            return;
                          }
                          onChange(item);
                        }}
                        title={getReminderModeTitle(item)}
                        caption={getReminderModeDescription(item)}
                      />
                    ))}

                    {value === ReminderMode.custom && (
                      <Stack direction="horizontal" gap="sm" items="center" className={"pl-7"}>
                        <Text>
                          {getReminderEveryTitle(
                            customPeriod ?? ReminderPeriod.day,
                            customFrequency ?? 1
                          )}
                        </Text>
                        <Controller
                          control={control}
                          name="custom_frequency"
                          render={({ field: { value, onChange } }) => (
                            <Select
                              variant="button"
                              emptyTitle={t("common:period")}
                              value={String(value ?? 1)}
                              onChange={(newValue) => onChange(Number(newValue))}
                              options={Array.from({ length: 6 }, (_, index) => ({
                                id: String(index + 1),
                                title: String(index + 1),
                              }))}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="custom_period"
                          render={({ field: { value, onChange } }) => (
                            <Select
                              key={`custom-period-${customFrequency ?? 1}`}
                              variant="button"
                              value={value ?? ReminderPeriod.day}
                              onChange={onChange}
                              emptyTitle={t("common:period")}
                              options={[
                                {
                                  id: ReminderPeriod.day,
                                  title: getReminderPeriodTitle(
                                    ReminderPeriod.day,
                                    customFrequency ?? 1
                                  ),
                                },
                                {
                                  id: ReminderPeriod.week,
                                  title: getReminderPeriodTitle(
                                    ReminderPeriod.week,
                                    customFrequency ?? 1
                                  ),
                                },
                                {
                                  id: ReminderPeriod.month,
                                  title: getReminderPeriodTitle(
                                    ReminderPeriod.month,
                                    customFrequency ?? 1
                                  ),
                                },
                              ]}
                            />
                          )}
                        />
                      </Stack>
                    )}
                  </Stack>
                )}
              />

              {(daysUntilDeadline > 0 || mode === ReminderMode.custom) && (
                <Stack direction={"horizontal"}>
                  <Tag variant={getReminderModeTagVariant(mode)}>
                    <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                      <FontAwesomeIcon icon="fa-light fa-bell" />
                      {mode === ReminderMode.custom
                        ? getReminderSettingsDescription({
                            channels,
                            custom_subject: null,
                            custom_body: null,
                            mode,
                            custom_frequency: customFrequency,
                            custom_period: customPeriod,
                          })
                        : t("core:reminder_count_info", {
                            reminders: t("plurals:reminders_count", {
                              count: reminderCount,
                            }),
                            days: t("plurals:plural_day", { count: daysUntilDeadline }),
                          })}
                    </Stack>
                  </Tag>
                </Stack>
              )}
            </Stack>
            <Stack className={"card-panel"} gap={"xl"}>
              <Title header={t("core:reminder_message")} />
              <Switch
                checked={customizeMessage}
                onChange={setCustomizeMessage}
                title={t("core:customize_reminder_message")}
                size={"sm"}
              />
              {customizeMessage && (
                <Stack gap={"sm"}>
                  <Controller
                    control={control}
                    name="custom_subject"
                    render={({ field: { value, onChange } }) => (
                      <InputWithVariableInsert
                        label={t("core:email_subject")}
                        value={value ?? ""}
                        onInput={(event) => onChange(event.currentTarget.value)}
                        placeholder={t("core:enter_custom_subject")}
                        variableOptions={reminderPlaceholders}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="custom_body"
                    render={({ field: { value, onChange } }) => (
                      <RichEditor
                        value={value ?? ""}
                        onChange={onChange}
                        placeholder={t("core:enter_custom_body")}
                        placeholderOptions={reminderPlaceholders}
                        minHeight={120}
                        hideHeadings
                      />
                    )}
                  />
                </Stack>
              )}
            </Stack>
          </>
        )}

        <Button onClick={onSubmit} size={"lg"} isLoading={updateReminders.isPending}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
}
